import { apiBlob, apiFetch, apiUpload } from './client'
import type {
  Project, Surcharge, APris,
  JournalEntry, Notification, ATASpec, CombinedEntry,
  CreateATAInput, StatusHistory,
  Attachment, AttachmentSend, SendToCustomerInput,
} from './types'

const DEFAULT_SURCHARGES: Surcharge[] = [
  { sortOrder: 1, typ: 'E-arvode 12% + arbetsledning 9%', procent: 0.2208 },
  { sortOrder: 2, typ: 'E-arvode 5% + arbetsledning 4%', procent: 0.092 },
  ...Array.from({ length: 6 }, (_, i) => ({ sortOrder: i + 3, typ: '', procent: 0 })),
]

let _projectId: number | null = null

export const service = {
  async init(): Promise<number> {
    const projects = await apiFetch<Project[]>('GET', '/projects')
    if (projects.length > 0) {
      _projectId = projects[0].id
    } else {
      const p = await apiFetch<Project>('POST', '/projects', {
        projektnr: '', projektNamn: '', bestellare: '', platschef: '',
        sign: '', ort: '', bestRef: '', bestTelefon: '', adress: '', postadress: '',
        bestProjektnr: '', bestEmail: '',
      })
      _projectId = p.id
      await apiFetch('PUT', `/projects/${_projectId}/surcharges`, DEFAULT_SURCHARGES)
    }
    return _projectId!
  },

  getProjectId(): number {
    if (!_projectId) throw new Error('service.init() not called')
    return _projectId
  },

  // ── Project ──────────────────────────────────────────────────────────────

  getProject: () => apiFetch<Project>('GET', `/projects/${_projectId}`),
  saveProject: (data: Partial<Project>) => apiFetch<{ id: number }>('PUT', `/projects/${_projectId}`, data),

  // ── Surcharges ────────────────────────────────────────────────────────────

  async getSurcharges(): Promise<Surcharge[]> {
    const rows = await apiFetch<Surcharge[]>('GET', `/projects/${_projectId}/surcharges`)
    if (rows.length === 0) return DEFAULT_SURCHARGES
    return Array.from({ length: 8 }, (_, i) => {
      const existing = rows.find((r) => r.sortOrder === i + 1)
      return existing ?? { sortOrder: i + 1, typ: '', procent: 0 }
    })
  },
  saveSurcharges: (items: Surcharge[]) =>
    apiFetch('PUT', `/projects/${_projectId}/surcharges`, items),

  // ── À-priser ──────────────────────────────────────────────────────────────

  getAPriser: () => apiFetch<APris[]>('GET', `/projects/${_projectId}/apriser`),
  saveAPriser: (items: APris[]) =>
    apiFetch('PUT', `/projects/${_projectId}/apriser`, items),

  // ── Journal Entries ───────────────────────────────────────────────────────

  getJournalEntries: () =>
    apiFetch<JournalEntry[]>('GET', `/projects/${_projectId}/journal`),

  async createEntry(): Promise<JournalEntry> {
    const entries = await service.getJournalEntries()
    const nextOrder = entries.length > 0
      ? Math.max(...entries.map((e) => e.sortOrder)) + 1
      : 1
    return apiFetch<JournalEntry>('POST', `/projects/${_projectId}/journal`, {
      sortOrder: nextOrder,
      arbeteBeskrivning: '', brevDagbok: '',
      grundkravBelopp: null, bestellarSynpunkter: '',
      faktureratDatum: null, faktureratBelopp: null,
    })
  },

  saveJournalEntry: (data: Partial<JournalEntry>) =>
    data.id
      ? apiFetch<{ id: number }>('PUT', `/projects/${_projectId}/journal/${data.id}`, data)
      : apiFetch<JournalEntry>('POST', `/projects/${_projectId}/journal`, data),

  deleteJournalEntry: (id: number) =>
    apiFetch('DELETE', `/projects/${_projectId}/journal/${id}`),

  async getNextSortOrder(): Promise<number> {
    const entries = await service.getJournalEntries()
    return entries.length > 0 ? Math.max(...entries.map((e) => e.sortOrder)) + 1 : 1
  },

  getStatusHistory: (sortOrder: number) =>
    apiFetch<StatusHistory[]>('GET', `/projects/${_projectId}/journal/${sortOrder}/history`),

  /** Create a journal entry (+ optional notification / spec) for a new ÄTA. */
  async createATA(input: CreateATAInput): Promise<number> {
    const sortOrder = await service.getNextSortOrder()
    const status = input.skapaUnderrattelse ? 'anmald' : (input.defaultStatus || 'upplagd')
    const today = new Date().toISOString().slice(0, 10)

    await service.saveJournalEntry({
      sortOrder,
      arbeteBeskrivning: input.rubrik,
      arbeteDetaljer: input.beskrivning ?? '',
      orsak: input.orsak ?? '',
      paverkanTid: input.paverkanTid ?? '',
      paverkanEkonomi: input.paverkanEkonomi ?? '',
      ansvarig: input.ansvarig ?? '',
      status,
      brevDagbok: '',
      grundkravBelopp: null,
      bestellarSynpunkter: '',
      faktureratDatum: null,
      faktureratBelopp: null,
    })

    const tasks: Promise<unknown>[] = []
    if (input.skapaUnderrattelse) {
      tasks.push(service.saveNotification({
        sortOrder,
        datum: today,
        omstandighet: input.beskrivning ?? '',
        ataMastUtforas: false, ataBorUtforas: false,
        kompProjektering: false, overGransvarde: false,
        vasentligRubbning: false, tidsrubbning: false,
        uppskattadKostnad: null, svarsDatum: null,
        bestellarSvar: 'Inväntar besked', bestellarSvarTyp: null,
        bestellarSignatur: null, bestellarDatum: null,
      }))
    }
    if (input.skapaSpec) {
      tasks.push(service.saveATASpec({
        sortOrder,
        datum: today,
        beskrivning: input.beskrivning ?? '',
        momsRate: 0.25,
        lineItems: [],
      }))
    }
    await Promise.all(tasks)
    return sortOrder
  },

  // ── Notifications ─────────────────────────────────────────────────────────

  getNotifications: () =>
    apiFetch<Notification[]>('GET', `/projects/${_projectId}/notifications`),

  saveNotification: (data: Partial<Notification>) =>
    data.id
      ? apiFetch<{ id: number }>('PUT', `/projects/${_projectId}/notifications/${data.id}`, data)
      : apiFetch<Notification>('POST', `/projects/${_projectId}/notifications`, data),

  deleteNotification: (id: number) =>
    apiFetch('DELETE', `/projects/${_projectId}/notifications/${id}`),

  // ── ÄTA Specs ─────────────────────────────────────────────────────────────

  getATASpecs: () =>
    apiFetch<ATASpec[]>('GET', `/projects/${_projectId}/ata`),

  saveATASpec: (data: Partial<ATASpec>) =>
    data.id
      ? apiFetch<{ id: number }>('PUT', `/projects/${_projectId}/ata/${data.id}`, data)
      : apiFetch<ATASpec>('POST', `/projects/${_projectId}/ata`, data),

  deleteATASpec: (id: number) =>
    apiFetch('DELETE', `/projects/${_projectId}/ata/${id}`),

  // ── Attachments & skicka-till-kund (#12357) ─────────────────────────────────

  getAttachments: (sortOrder: number) =>
    apiFetch<Attachment[]>('GET', `/projects/${_projectId}/journal/${sortOrder}/attachments`),

  uploadAttachments(sortOrder: number, files: FileList | File[]): Promise<Attachment[]> {
    const form = new FormData()
    Array.from(files).forEach((f) => form.append('files', f))
    return apiUpload<Attachment[]>(
      `/projects/${_projectId}/journal/${sortOrder}/attachments`, form,
    )
  },

  deleteAttachment: (id: number) =>
    apiFetch('DELETE', `/projects/${_projectId}/attachments/${id}`),

  downloadAttachment: (id: number) =>
    apiBlob(`/projects/${_projectId}/attachments/${id}/content`),

  sendToCustomer: (sortOrder: number, payload: SendToCustomerInput) =>
    apiFetch<{ id: number; status: string }>(
      'POST', `/projects/${_projectId}/journal/${sortOrder}/send`, payload,
    ),

  getSends: (sortOrder: number) =>
    apiFetch<AttachmentSend[]>('GET', `/projects/${_projectId}/journal/${sortOrder}/sends`),

  // ── Combined helpers ──────────────────────────────────────────────────────

  async getAllEntries(): Promise<CombinedEntry[]> {
    const [journals, notifications, ataSpecs] = await Promise.all([
      service.getJournalEntries(),
      service.getNotifications(),
      service.getATASpecs(),
    ])
    const orders = [
      ...new Set([
        ...journals.map((e) => e.sortOrder),
        ...notifications.map((n) => n.sortOrder),
        ...ataSpecs.map((s) => s.sortOrder),
      ]),
    ].sort((a, b) => a - b)
    return orders.map((sortOrder) => ({
      sortOrder,
      journal: journals.find((e) => e.sortOrder === sortOrder) ?? null,
      notification: notifications.find((n) => n.sortOrder === sortOrder) ?? null,
      ataSpec: ataSpecs.find((s) => s.sortOrder === sortOrder) ?? null,
    }))
  },

  async deleteEntry(entry: CombinedEntry): Promise<void> {
    await Promise.all([
      entry.journal?.id && service.deleteJournalEntry(entry.journal.id),
      entry.notification?.id && service.deleteNotification(entry.notification.id),
      entry.ataSpec?.id && service.deleteATASpec(entry.ataSpec.id),
    ].filter(Boolean))
  },
}
